
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { TJob } from "apiTypes/jobs";
import { pickLocale } from "components/Jobs/jobLocale";
import SEO from "components/Seo";
import { GetServerSideProps } from "next";
import useTranslation from "next-translate/useTranslation";
import Link from "next/link";
import { useRouter } from "next/router";
import React from "react";
import styled from "styled-components";
import { getJobById } from "utils/jobs";
type Props = {
    job: TJob;
};
const JobDetail = ({ job }: Props) => {
    const { t } = useTranslation("common");
    const { locale } = useRouter();
    const title = pickLocale(job.title, locale);
    const description = pickLocale(job.description, locale);
    const urgentNote = pickLocale(job.urgent_hiring_note, locale);
    // content is rich-text HTML — only render if non-null for the active locale.
    const contentHtml = job.content?.[locale as keyof typeof job.content]?.value
        ?? job.content?.en?.value
        ?? null;
    const isExpired = job.deadline ? new Date(job.deadline).getTime() < Date.now() : false;
    const deadlineDisplay = job.deadline ? job.deadline.split(" ")[0] : null;
    const metaDescription = description
        ? description.replace(/\s+/g, " ").trim().slice(0, 160)
        : undefined;
    return (<>
      <SEO title={title} description={metaDescription} additinalProps={isExpired ? { noindex: true, nofollow: true } : undefined}/>
      <DetailContainer>
        <div className="container py-5">
          <BackLink href="/jobs">
            <i className="fe-arrow-left me-1"/>
            {t("back_to_jobs")}
          </BackLink>

          {/* Urgent banner */}
          {urgentNote && (<UrgentBanner role="alert">
              <i className="fe-alert-circle me-2"/>
              {urgentNote}
            </UrgentBanner>)}

          {/* Header */}
          <Header>
            <JobTitle>{title}</JobTitle>
            <MetaRow>
              {job.location && (<MetaItem>
                  <i className="fe-map-pin me-1"/>
                  {job.location}
                </MetaItem>)}
              {deadlineDisplay && (<DeadlineItem expired={isExpired}>
                  <i className="fe-calendar me-1"/>
                  {t("deadline")}: <strong>{deadlineDisplay}</strong>
                  {isExpired && <ExpiredTag>{t("expired")}</ExpiredTag>}
                </DeadlineItem>)}
            </MetaRow>
          </Header>

          <ContentLayout>
            <MainContent>
              {/* Description */}
              {description && (<Section>
                  <SectionTitle>{t("job_overview")}</SectionTitle>
                  <Description>{description}</Description>
                </Section>)}

              {/* Rich-text content block */}
              {contentHtml && (<Section>
                  <RichContent dangerouslySetInnerHTML={{ __html: contentHtml }}/>
                </Section>)}

              {/* Preferred certifications */}
              {job.preferred_certifications && (<Section>
                  <SectionTitle>{t("job_preferred_certifications")}</SectionTitle>
                  <Description>{job.preferred_certifications}</Description>
                </Section>)}
            </MainContent>

            {/* Apply sidebar — plain anchor, external Google Form */}
            <Sidebar>
              <ApplyCard>
                {isExpired || !job.application_url ? (<ClosedNote>{t("job_closed_note")}</ClosedNote>) : (<ApplyButton href={job.application_url} target="_blank" rel="noopener noreferrer">
                    {t("apply_external")}
                    <i className="fe-external-link ms-2"/>
                  </ApplyButton>)}
              </ApplyCard>
            </Sidebar>
          </ContentLayout>
        </div>
      </DetailContainer>
    </>);
};
export default JobDetail;
const getServerSideProps: GetServerSideProps = async (context) => {
    const id = context.params?.id as string;
    const job = await getJobById(id);
    if (!job) {
        return { notFound: true };
    }
    return { props: { job } };
};
// Styled Components
const DetailContainer = styled.div `
  min-height: 100vh;
  background-color: var(--bs-body-bg);
`;
const BackLink = styled(Link) `
  display: inline-flex;
  align-items: center;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--bs-primary);
  text-decoration: none;
  margin-bottom: 1.5rem;

  &:hover {
    text-decoration: underline;
  }
`;
const UrgentBanner = styled.div `
  display: flex;
  align-items: center;
  padding: 0.875rem 1.25rem;
  border-radius: 0.5rem;
  background-color: #fff3cd;
  color: #856404;
  font-weight: 600;
  margin-bottom: 1.5rem;
`;
const Header = styled.div `
  margin-bottom: 2rem;
`;
const JobTitle = styled.h1 `
  font-size: 2.25rem;
  font-weight: 700;
  color: var(--bs-heading-color);
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;
const MetaRow = styled.div `
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1.5rem;
`;
const MetaItem = styled.div `
  display: flex;
  align-items: center;
  font-size: 1rem;
  color: var(--bs-secondary-color);
`;
const DeadlineItem = styled.div<{
    expired: boolean;
}> `
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  color: ${({ expired }) => (expired ? "var(--bs-danger)" : "var(--bs-secondary-color)")};
`;
const ExpiredTag = styled.span `
  padding: 0.15rem 0.6rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  background-color: #f8d7da;
  color: #721c24;
`;
const ContentLayout = styled.div `
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: 2rem;
  align-items: start;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;
const MainContent = styled.div `
  min-width: 0;
`;
const Section = styled.section `
  margin-bottom: 2rem;
`;
const SectionTitle = styled.h2 `
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--bs-heading-color);
  margin-bottom: 0.75rem;
`;
const Description = styled.p `
  font-size: 1rem;
  line-height: 1.7;
  color: var(--bs-body-color);
  white-space: pre-line;
`;
const RichContent = styled.div `
  font-size: 1rem;
  line-height: 1.7;
  color: var(--bs-body-color);

  p {
    margin-bottom: 0.75rem;
  }

  ul,
  ol {
    padding-inline-start: 1.25rem;
    margin-bottom: 0.75rem;

    li {
      margin-bottom: 0.4rem;
    }
  }

  h2,
  h3,
  h4 {
    font-weight: 700;
    color: var(--bs-heading-color);
    margin-top: 1.25rem;
    margin-bottom: 0.5rem;
  }
`;
const Sidebar = styled.aside `
  position: sticky;
  top: 1.5rem;

  @media (max-width: 992px) {
    position: static;
  }
`;
const ApplyCard = styled.div `
  border: 1px solid var(--bs-border-color);
  border-radius: 0.75rem;
  padding: 1.5rem;
  background-color: var(--bs-card-bg);
`;
const ApplyButton = styled.a `
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  background-color: var(--bs-primary);
  color: #fff;
  font-weight: 600;
  text-decoration: none;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
    color: #fff;
  }
`;
const ClosedNote = styled.p `
  margin: 0;
  text-align: center;
  font-weight: 600;
  color: var(--bs-secondary-color);
`;

    async function __Next_Translate__getServerSideProps__1a13f66c043__(ctx) {
      const res = await getServerSideProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/jobs/[id]',
            loaderName: 'getServerSideProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || (() => Promise.resolve({})),
          }))
        }
      }
    }
    export { __Next_Translate__getServerSideProps__1a13f66c043__ as getServerSideProps }
  